import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import { motion } from "framer-motion";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import { HtmlTooltip } from "../../../Theme";
import FavouriteDrugTooltip from "./FavouriteDrugTooltip";

export interface FavouriteDrugItem {
  description?: string;
  drugName?: string;
  strength?: string;
  strengthUnit?: string;
  dosageForm?: string;
  quantity?: number | string;
  quantityQualifier?: string;
  refills?: number | string;
  pharmacyName?: string;
  sig?: string;
  [key: string]: any;
}

interface FavouriteDrugsLookupProps {
  drugs: (FavouriteDrugItem | string)[];
  primaryColor: string;
  /** Called with the full favourite item so the caller can build the draft. */
  onDrugSelect: (drug: FavouriteDrugItem) => void;
  label?: string;
  /** True on a past (non-latest) turn: render read-only. */
  disabled?: boolean;
}

/** Label shown on the chip — prefer the API `description`, else compose one. */
function drugLabel(drug: FavouriteDrugItem): string {
  if (drug.description) return drug.description;
  const parts = [
    drug.drugName,
    [drug.strength, drug.strengthUnit].filter(Boolean).join(" "),
    drug.route,
    drug.dosageForm,
  ].filter(Boolean);
  return parts.join(" ") || "Unknown drug";
}

/**
 * Renders the doctor's favourite (saved) drugs as selectable chips. The chip
 * label is the drug description; hovering a chip reveals quantity, refills,
 * pharmacy and sig (see FavouriteDrugTooltip). Selecting a chip hands the full
 * item back so a ready-to-confirm prescription draft can be built from it.
 */
const FavouriteDrugsLookup: React.FC<FavouriteDrugsLookupProps> = ({
  drugs,
  primaryColor,
  onDrugSelect,
  label = "Your favourite drugs",
  disabled = false,
}) => {
  const normalized: FavouriteDrugItem[] = (drugs || []).map((d) =>
    typeof d === "string" ? { description: d } : d
  );

  if (normalized.length === 0) return null;

  const chipStyles = {
    backgroundColor: `${primaryColor}15`,
    color: primaryColor,
    border: `1px solid ${primaryColor}40`,
    cursor: disabled ? "default" : "pointer",
    transition: "all 0.2s ease",
    ...(disabled
      ? {}
      : {
          "&:hover": {
            backgroundColor: `${primaryColor}25`,
            border: `1px solid ${primaryColor}60`,
            transform: "translateY(-1px)",
            boxShadow: `0 2px 4px ${primaryColor}20`,
          },
          "&:active": { transform: "translateY(0)" },
        }),
    "& .MuiChip-icon": { color: primaryColor },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          width: "100%",
          mt: 2,
          mb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {label && (
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {normalized.map((drug, index) => (
            <HtmlTooltip
              key={index}
              placement="top"
              arrow
              title={<FavouriteDrugTooltip row={drug} />}
            >
              <Chip
                icon={<MedicationOutlinedIcon />}
                label={drugLabel(drug)}
                onClick={disabled ? undefined : () => onDrugSelect(drug)}
                disabled={disabled}
                sx={chipStyles}
              />
            </HtmlTooltip>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
};

export default FavouriteDrugsLookup;
